import { createContext, useContext } from 'react'

import type { NavLocation } from '@/components/layout/nav-groups'

/**
 * True under AppShell. The router's DEFAULT pending and error components are
 * one component each for every route in the tree, but they land in two very
 * different places: inside the signed-in shell (a screen's main column, next
 * to the rail) or outside it (the auth and onboarding screens, and the `_app`
 * layout itself before its chunk has loaded). Only the first may draw a
 * ScreenLayout — the shared rail inside it makes signed-in requests, and the
 * second place has no nav around it to sit beside.
 *
 * Its own module, not an export of AppShell.tsx, so that file keeps exporting
 * a component and nothing else (react-refresh/only-export-components).
 */
export const InShellContext = createContext(false)

export const useInShell = (): boolean => useContext(InShellContext)

/**
 * The URL, under AppShell: what ScreenLayout reads to draw the page's section
 * tab strip (nav-groups.ts `sectionTabs`). `null` outside the shell — the
 * auth and onboarding screens, and a component test with no router — where
 * no strip is drawn.
 *
 * A context the shell fills, rather than a router read in ScreenLayout,
 * because ScreenLayout also renders where there is no router at all, and a
 * router hook there would throw.
 */
export const ShellLocationContext = createContext<NavLocation | null>(null)

export const useShellLocation = (): NavLocation | null => useContext(ShellLocationContext)
